import React, { memo } from "react";

import { classNames, transformDateToLocalDateString, useTranslate } from "helpers";
import { FLEET_VEHICLE_STATUS_CONFIG, ROOT_STATUSES } from "services/api/fleet/vehicles/consts";
import { FleetVehicleStatusEnum } from "services/api/fleet/vehicles/types";
import { ShowcaseCard, ShowcaseIntro, ShowcaseNotice } from "ui/components/Showcase";

import { StatusPillProps, VehicleStatusIntroProps } from "./types";

/** Značka statusa u boji statusa, ista kao u ostatku aplikacije. */
const StatusPill: React.FC<StatusPillProps> = ({ status, label }) => {
  const config = FLEET_VEHICLE_STATUS_CONFIG[status];
  return (
    <span className={classNames("inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[0.65rem] font-semibold", config.color)}>
      <span className={classNames("w-1.5 h-1.5 rounded-full", config.dotColor)} />
      {label}
    </span>
  );
};

/**
 * Lijevi panel promjene statusa vozila: prvo popis statusa sa sadašnjim i novim, zatim
 * podaci uz novi status i zapis u povijesti.
 */
const VehicleStatusIntro: React.FC<VehicleStatusIntroProps> = ({
  currentStatus,
  selectedStatus,
  formValues,
  isDetailStep,
  isStatusLocked,
}) => {
  const { t } = useTranslate();
  const statusLabel = (status: FleetVehicleStatusEnum) => t(FLEET_VEHICLE_STATUS_CONFIG[status].label);
  const newStatus = selectedStatus ?? currentStatus;
  const hasChange = !!selectedStatus && selectedStatus !== currentStatus;
  const fromDate = transformDateToLocalDateString(formValues.validFrom ?? new Date());
  const untilDate = formValues.validUntil ? transformDateToLocalDateString(formValues.validUntil) : null;

  return (
    <ShowcaseIntro
      sceneKey={isDetailStep ? "detail" : "pick"}
      title={isDetailStep ? "Još samo nekoliko podataka" : "Gdje je vozilo sada?"}
      description={
        isDetailStep
          ? "Zabilježite od kada vrijedi status, do kada i zbog čega, da se poslije zna što se dogodilo."
          : "Status govori je li vozilo dostupno i zašto nije. Svaka promjena ostaje zapisana u povijesti vozila."
      }
      stepCurrent={isStatusLocked ? undefined : isDetailStep ? 1 : 0}
      stepCount={isStatusLocked ? undefined : 2}
    >
      {!isDetailStep ? (
        <>
          <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
            <div className="p-5">
              <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Status vozila</div>
              <div className="mt-3 space-y-1.5">
                {ROOT_STATUSES.map((status) => {
                  const config = FLEET_VEHICLE_STATUS_CONFIG[status];
                  const isCurrent = status === currentStatus || config.subStatuses?.includes(currentStatus);
                  const isNew =
                    hasChange && (status === selectedStatus || !!(selectedStatus && config.subStatuses?.includes(selectedStatus)));

                  return (
                    <div
                      key={status}
                      className={classNames(
                        "flex items-center gap-2 rounded-md px-2 py-1.5",
                        isNew ? "bg-blue-50 ring-1 ring-blue-200" : isCurrent ? "bg-slate-50" : ""
                      )}
                    >
                      <span className={classNames("w-2 h-2 rounded-full flex-shrink-0", config.dotColor)} />
                      <span className="flex-1 text-[0.7rem] font-medium text-slate-700 truncate">{statusLabel(status)}</span>
                      {isNew && <span className="text-[0.6rem] font-semibold text-blue-600">Novo</span>}
                      {isCurrent && !isNew && <span className="text-[0.6rem] font-semibold text-slate-400">Sada</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </ShowcaseCard>

          <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
            <div className="p-5">
              <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Promjena</div>
              <div className="mt-3 space-y-2">
                <StatusPill status={currentStatus} label={statusLabel(currentStatus)} />
                <div className="pl-3 text-slate-300">↓</div>
                {hasChange ? (
                  <StatusPill status={newStatus} label={statusLabel(newStatus)} />
                ) : (
                  <span className="text-[0.65rem] text-slate-400">Odaberite novi status</span>
                )}
              </div>
            </div>
          </ShowcaseCard>
        </>
      ) : (
        <>
          <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
            <div className="p-5">
              <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Novi status</div>
              <div className="mt-2">
                <StatusPill status={newStatus} label={statusLabel(newStatus)} />
              </div>
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
                  <span className="text-[0.7rem] text-slate-500">Vrijedi od</span>
                  <span className="text-[0.7rem] font-semibold text-slate-800">{fromDate}</span>
                </div>
                <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
                  <span className="text-[0.7rem] text-slate-500">Vrijedi do</span>
                  <span className="text-[0.7rem] font-semibold text-slate-800">{untilDate ?? "Do promjene"}</span>
                </div>
              </div>
              <p className="mt-3 text-[0.65rem] leading-relaxed text-slate-500 line-clamp-3">
                {formValues.note || "Napomena o razlogu promjene statusa."}
              </p>
            </div>
          </ShowcaseCard>

          <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
            <div className="p-5">
              <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Povijest statusa</div>
              <div className="mt-3 space-y-2 text-[0.65rem]">
                <div className="flex items-center gap-2 text-slate-400">
                  <span className={classNames("w-2 h-2 rounded-full", FLEET_VEHICLE_STATUS_CONFIG[currentStatus].dotColor)} />
                  <span className="truncate">{statusLabel(currentStatus)}</span>
                </div>
                <div className="flex items-center gap-2 font-semibold text-slate-700">
                  <span className={classNames("w-2 h-2 rounded-full", FLEET_VEHICLE_STATUS_CONFIG[newStatus].dotColor)} />
                  <span className="truncate">{statusLabel(newStatus)}</span>
                  <span className="ml-auto font-normal text-slate-400">{fromDate}</span>
                </div>
              </div>
            </div>
          </ShowcaseCard>
        </>
      )}

      <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
        <ShowcaseNotice
          title="Zapisano u povijesti vozila"
          subtitle="Svaka promjena s datumom i razlogom"
          iconClassName="bg-blue-100"
          icon={
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#1D4ED8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 12a9 9 0 109-9 9.75 9.75 0 00-6.74 2.74L3 8M3 3v5h5M12 7v5l4 2" />
            </svg>
          }
        />
      </ShowcaseCard>
    </ShowcaseIntro>
  );
};

export default memo(VehicleStatusIntro);
